import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface ProductUpdate {
  ndc: string;
  product_name: string;
  manufacturer: string;
  coo_country: string;
  coo_score: number;
  quality_tier: string;
  factor_details: FactorDetail[];
  factor_results: Record<string, boolean>;
  scored_at: string;
  run_id: string;
}

interface FactorDetail {
  source: string;
  factor: string;
  hit: boolean;
  weight: number;
  category: string;
  result?: string;
  raw_inputs?: Record<string, unknown>;
  raw_output?: boolean;
  disabled?: boolean;
}

// ============================================================
// STEWARD DISPLAY NAMES
// ============================================================

const STEWARD_DISPLAY: Record<string, string> = {
  steward_fda_establishments: 'FDA Establishments',
  steward_openfda_ndc: 'OpenFDA NDC',
  steward_fda_import_refusals: 'FDA Import Refusals',
  steward_epa_tri: 'EPA TRI',
  steward_fda_recalls: 'FDA Recalls',
  steward_dailymed_spl: 'DailyMed SPL',
  steward_package_ocr: 'Package OCR',
  steward_sec_edgar: 'SEC EDGAR',
  steward_drugs_at_fda: 'Drugs@FDA',
  steward_fda_registration: 'FDA Registration',
  steward_who_prequalification: 'WHO Prequalification',
  steward_dmf_pharmacompass: 'DMF PharmaCompass',
  steward_fda_ndc_directory: 'FDA NDC Directory',
  steward_fda_drug_shortages: 'FDA Drug Shortages',
  steward_pricing: 'Pricing (NADAC)',
  steward_rxnorm: 'RxNorm',
  steward_fda_label_api: 'FDA Label API',
  steward_haiku_spl: 'Haiku SPL Analysis',
  steward_fec_donations: 'FEC Donations',
  steward_lobbying: 'Lobbying (Senate LDA)',
  steward_politician_holdings: 'Politician Holdings',
  steward_pac_contributions: 'PAC Contributions',
  steward_legislation: 'Legislation',
  steward_ccp_flags: 'CCP Flags',
  steward_manufacturer_name_geo: 'Manufacturer Geo',
};

// ============================================================
// PER-STEWARD COLUMN DEFINITIONS
// Each steward shows NDC #, Product Name, plus its own relevant
// data columns, plus Data Completeness % and Updated At.
// ============================================================

interface StewardColumn {
  key: string;
  label: string;
  /** Extract cell value from factor_details / factor_results / product */
  render: (product: ProductUpdate) => string;
}

function factorHit(product: ProductUpdate, factorName: string): string {
  const detail = product.factor_details?.find(f => f.factor === factorName);
  if (!detail) return '--';
  return detail.hit ? 'Yes' : 'No';
}

function factorResult(product: ProductUpdate, factorName: string): string {
  const detail = product.factor_details?.find(f => f.factor === factorName);
  if (!detail) return '--';
  if (detail.result) return detail.result;
  return detail.hit ? 'Yes' : 'No';
}

const STEWARD_COLUMNS: Record<string, StewardColumn[]> = {
  steward_fda_establishments: [
    { key: 'facility_found', label: 'Facility Found', render: p => factorResult(p, 'has_registered_manufacturing_facility') },
    { key: 'fdf_mfg', label: 'FDF Manufacture', render: p => factorHit(p, 'has_fdf_manufacture_operation') },
    { key: 'country_match', label: 'Country Match', render: p => factorResult(p, 'facility_country_matches_coo') },
    { key: 'current', label: 'Registration Current', render: p => factorHit(p, 'registration_is_current') },
    { key: 'single_country', label: 'Single Country', render: p => factorHit(p, 'single_country_manufacturing') },
  ],
  steward_openfda_ndc: [
    { key: 'ndc_found', label: 'NDC Found', render: p => factorHit(p, 'ndc_found_in_directory') },
    { key: 'labeler', label: 'Labeler Name', render: p => factorResult(p, 'labeler_name_resolved') },
    { key: 'labeler_match', label: 'Labeler Match', render: p => factorHit(p, 'labeler_country_matches_coo') },
    { key: 'marketing', label: 'Marketing Status', render: p => factorResult(p, 'marketing_status_active') },
  ],
  steward_fda_import_refusals: [
    { key: 'refusal_found', label: 'Refusal Found', render: p => factorResult(p, 'import_refusal_found') },
    { key: 'recent', label: 'Recent Refusal', render: p => factorResult(p, 'refusal_is_recent') },
    { key: 'country', label: 'Country Identified', render: p => factorResult(p, 'refusal_country_identified') },
    { key: 'charge', label: 'Charge Severity', render: p => factorResult(p, 'refusal_charge_severity') },
  ],
  steward_epa_tri: [
    { key: 'facility_found', label: 'TRI Facility', render: p => factorResult(p, 'tri_facility_found') },
    { key: 'pharma', label: 'Pharma NAICS', render: p => factorHit(p, 'facility_is_pharma_naics') },
    { key: 'us', label: 'In United States', render: p => factorResult(p, 'facility_in_united_states') },
    { key: 'chemical', label: 'Chemical Data', render: p => factorHit(p, 'chemical_data_consistent') },
  ],
  steward_fda_recalls: [
    { key: 'recall_found', label: 'Recall Found', render: p => factorResult(p, 'recall_record_found') },
    { key: 'recent', label: 'Recent', render: p => factorResult(p, 'recall_is_recent') },
    { key: 'classification', label: 'Classification', render: p => factorResult(p, 'recall_classification_severe') },
    { key: 'country', label: 'Country', render: p => factorResult(p, 'recall_country_identified') },
  ],
  steward_dailymed_spl: [
    { key: 'country', label: 'Country Found', render: p => factorResult(p, 'spl_country_found') },
    { key: 'mfg_info', label: 'Mfg Info Found', render: p => factorHit(p, 'spl_manufacturing_info_found') },
    { key: 'mfr_name', label: 'Mfr Name', render: p => factorResult(p, 'spl_manufacturer_name_found') },
    { key: 'context', label: 'Context Clear', render: p => factorHit(p, 'spl_manufacturing_context_clear') },
    { key: 'coo_match', label: 'COO Match', render: p => factorResult(p, 'spl_country_matches_coo') },
  ],
  steward_package_ocr: [
    { key: 'image', label: 'Image Found', render: p => factorHit(p, 'package_image_found') },
    { key: 'text', label: 'Text Extracted', render: p => factorHit(p, 'package_text_extracted') },
    { key: 'country', label: 'Country', render: p => factorResult(p, 'package_country_identified') },
    { key: 'signal', label: 'Signal Type', render: p => factorResult(p, 'package_signal_type_identified') },
    { key: 'context', label: 'Context Clear', render: p => factorHit(p, 'package_manufacturing_context_clear') },
  ],
  steward_sec_edgar: [
    { key: 'filing_found', label: 'Filing Found', render: p => factorHit(p, 'sec_filing_found') },
    { key: 'mfg_section', label: 'Mfg Section', render: p => factorHit(p, 'sec_manufacturing_section_found') },
    { key: 'country', label: 'Country', render: p => factorResult(p, 'sec_country_identified') },
    { key: 'coo_match', label: 'COO Match', render: p => factorHit(p, 'sec_country_matches_coo') },
    { key: 'details', label: 'Details Clear', render: p => factorHit(p, 'sec_manufacturing_details_clear') },
  ],
  steward_drugs_at_fda: [
    { key: 'app_found', label: 'Application Found', render: p => factorResult(p, 'drug_application_found') },
    { key: 'app_type', label: 'App Type', render: p => factorResult(p, 'application_type_identified') },
    { key: 'country', label: 'Country Match', render: p => factorResult(p, 'application_country_matches_coo') },
    { key: 'active', label: 'Status Active', render: p => factorResult(p, 'application_status_active') },
  ],
  steward_fda_registration: [
    { key: 'reg_found', label: 'Registration Found', render: p => factorResult(p, 'registration_found') },
    { key: 'country_id', label: 'Country Identified', render: p => factorResult(p, 'registration_country_identified') },
    { key: 'country', label: 'Country Match', render: p => factorHit(p, 'registration_country_matches_coo') },
    { key: 'orange_book', label: 'Orange Book', render: p => factorResult(p, 'orange_book_listing_found') },
  ],
  steward_who_prequalification: [
    { key: 'pq_found', label: 'PQ Listing', render: p => factorResult(p, 'who_pq_listing_found') },
    { key: 'site', label: 'Site Identified', render: p => factorResult(p, 'who_pq_manufacturing_site_identified') },
    { key: 'country', label: 'Country Match', render: p => factorResult(p, 'who_pq_country_matches_coo') },
  ],
  steward_dmf_pharmacompass: [
    { key: 'dmf_found', label: 'DMF Record', render: p => factorResult(p, 'dmf_record_found') },
    { key: 'site', label: 'Site Identified', render: p => factorResult(p, 'dmf_manufacturing_site_identified') },
    { key: 'country', label: 'Country Match', render: p => factorResult(p, 'dmf_country_matches_coo') },
    { key: 'pc_data', label: 'PharmaCompass Data', render: p => factorResult(p, 'pharmacompass_data_available') },
  ],
  steward_fda_ndc_directory: [
    { key: 'product_found', label: 'Product Found', render: p => factorHit(p, 'ndc_product_found') },
    { key: 'brand', label: 'Brand Name', render: p => factorResult(p, 'brand_name_resolved') },
    { key: 'generic', label: 'Generic Name', render: p => factorResult(p, 'generic_name_resolved') },
    { key: 'labeler', label: 'Labeler', render: p => factorResult(p, 'labeler_name_resolved') },
    { key: 'marketing', label: 'Marketing Status', render: p => factorResult(p, 'marketing_status_identified') },
  ],
  steward_fda_drug_shortages: [
    { key: 'alert_found', label: 'Alert Found', render: p => factorHit(p, 'shortage_alert_found') },
    { key: 'status', label: 'Status', render: p => factorResult(p, 'shortage_status_identified') },
    { key: 'severity', label: 'Severity', render: p => factorResult(p, 'shortage_severity_assessed') },
    { key: 'reason', label: 'Reason', render: p => factorResult(p, 'shortage_reason_identified') },
  ],
  steward_pricing: [
    { key: 'price_found', label: 'NADAC Price', render: p => factorResult(p, 'nadac_price_found') },
    { key: 'unit', label: 'Unit', render: p => factorResult(p, 'pricing_unit_identified') },
    { key: 'current', label: 'Date Current', render: p => factorResult(p, 'effective_date_current') },
  ],
  steward_rxnorm: [
    { key: 'rxcui', label: 'RxCUI Found', render: p => factorHit(p, 'rxcui_found') },
    { key: 'clinical', label: 'Clinical Drug', render: p => factorHit(p, 'clinical_drug_linked') },
    { key: 'branded', label: 'Branded Drug', render: p => factorHit(p, 'branded_drug_linked') },
    { key: 'ingredient', label: 'Ingredient', render: p => factorResult(p, 'ingredient_identified') },
  ],
  steward_fda_label_api: [
    { key: 'label_found', label: 'Label Found', render: p => factorHit(p, 'label_found') },
    { key: 'country', label: 'Country Mention', render: p => factorResult(p, 'country_mention_in_label') },
    { key: 'manufacturer', label: 'Mfr Resolved', render: p => factorResult(p, 'manufacturer_name_resolved') },
    { key: 'sections', label: 'Sections Checked', render: p => factorResult(p, 'multiple_sections_checked') },
  ],
  steward_haiku_spl: [
    { key: 'deception', label: 'Deception Pattern', render: p => factorResult(p, 'deception_pattern_detected') },
    { key: 'mfr_by', label: 'Mfr By', render: p => factorResult(p, 'manufacturer_by_identified') },
    { key: 'mfr_for', label: 'Mfr For', render: p => factorResult(p, 'manufacturer_for_identified') },
    { key: 'countries', label: 'Countries', render: p => factorResult(p, 'countries_mentioned') },
    { key: 'confidence', label: 'Confidence', render: p => factorResult(p, 'confidence_score_assigned') },
  ],
  steward_fec_donations: [
    { key: 'records', label: 'Records Found', render: p => factorHit(p, 'donation_records_found') },
    { key: 'donor', label: 'Donor Matched', render: p => factorHit(p, 'donor_name_matched') },
    { key: 'party', label: 'Party', render: p => factorResult(p, 'recipient_party_identified') },
    { key: 'significant', label: 'Significant', render: p => factorHit(p, 'donation_amount_significant') },
  ],
  steward_lobbying: [
    { key: 'client', label: 'Client Matched', render: p => factorHit(p, 'lobbying_client_matched') },
    { key: 'filings', label: 'Filings Found', render: p => factorHit(p, 'filing_records_found') },
    { key: 'registrant', label: 'Registrant', render: p => factorResult(p, 'registrant_identified') },
    { key: 'income', label: 'Income Reported', render: p => factorHit(p, 'income_expenses_reported') },
  ],
  steward_politician_holdings: [
    { key: 'ptr', label: 'PTR Filing', render: p => factorHit(p, 'ptr_filing_found') },
    { key: 'asset', label: 'Pharma Asset', render: p => factorHit(p, 'pharma_asset_matched') },
    { key: 'member', label: 'Member', render: p => factorResult(p, 'member_identified') },
    { key: 'txn', label: 'Transaction', render: p => factorResult(p, 'transaction_type_identified') },
  ],
  steward_pac_contributions: [
    { key: 'pac', label: 'PAC Found', render: p => factorHit(p, 'pac_committee_found') },
    { key: 'disbursements', label: 'Disbursements', render: p => factorHit(p, 'disbursement_records_found') },
    { key: 'recipient', label: 'Recipient', render: p => factorResult(p, 'recipient_identified') },
    { key: 'total', label: 'Total Disbursed', render: p => factorResult(p, 'total_disbursed_calculated') },
  ],
  steward_legislation: [
    { key: 'bill', label: 'Bill Found', render: p => factorHit(p, 'relevant_bill_found') },
    { key: 'status', label: 'Bill Status', render: p => factorResult(p, 'bill_status_tracked') },
    { key: 'keyword', label: 'Keyword Match', render: p => factorHit(p, 'pharma_keyword_matched') },
  ],
  steward_ccp_flags: [
    { key: 'dod', label: 'DOD 1260H', render: p => factorHit(p, 'dod_1260h_match') },
    { key: 'nscmic', label: 'NS-CMIC', render: p => factorHit(p, 'ns_cmic_match') },
    { key: 'uflpa', label: 'UFLPA', render: p => factorHit(p, 'uflpa_match') },
    { key: 'ofac', label: 'OFAC SDN', render: p => factorHit(p, 'csl_ofac_match') },
    { key: 'flag', label: 'CCP Flag', render: p => factorHit(p, 'ccp_flag_set') },
  ],
  steward_manufacturer_name_geo: [
    { key: 'geo_match', label: 'Geo Match', render: p => factorHit(p, 'manufacturer_name_geo') },
  ],
};

// ============================================================
// HELPERS
// ============================================================

function formatTimestamp(iso: string): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function computeCompleteness(product: ProductUpdate): number {
  const details = product.factor_details || [];
  if (details.length === 0) return 0;
  const enabled = details.filter(f => !f.disabled);
  if (enabled.length === 0) return 0;
  const hits = enabled.filter(f => f.hit).length;
  return Math.round((hits / enabled.length) * 100);
}

function completenessColor(pct: number): string {
  if (pct >= 80) return '#22C55E';
  if (pct >= 50) return '#F59E0B';
  return '#EF4444';
}

// ============================================================
// COMPONENT
// ============================================================

export default function StewardProductUpdates() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const [products, setProducts] = useState<ProductUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const agentDisplay = STEWARD_DISPLAY[agentName || ''] || (agentName || '').replace(/_/g, ' ');
  const stewardColumns = STEWARD_COLUMNS[agentName || ''] || [];

  const fetchProducts = useCallback(async () => {
    if (!agentName) return;
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/agents/${agentName}/collection-log?status=completed`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data = await resp.json();
        setProducts(data.products || []);
        setError(null);
      } else {
        setError('Failed to fetch product updates');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch product updates');
    } finally {
      setLoading(false);
    }
  }, [agentName]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${T.border}`,
            borderTopColor: T.gold, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading product updates...</span>
        </div>
      </div>
    );
  }

  // Compute overall data completeness
  const overallCompleteness = products.length > 0
    ? Math.round(products.reduce((sum, p) => sum + computeCompleteness(p), 0) / products.length)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Back link + Header */}
      <div>
        <button
          onClick={() => navigate(`/agent-detail/${agentName}`)}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to {agentDisplay}
        </button>

        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, margin: 0, marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Product Updates
            </h1>
            <p style={{ fontSize: 14, color: T.textMid, maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              Data collection log for the {agentDisplay} agent. Click a row to see collected evidence details.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              background: `${T.navy}08`, borderRadius: 12, padding: '16px 24px',
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
                Total Updates
              </span>
              <span style={{ fontSize: 32, fontWeight: 800, color: T.textDark }}>
                {products.length}
              </span>
            </div>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              background: `${T.navy}08`, borderRadius: 12, padding: '16px 24px',
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: completenessColor(overallCompleteness), textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
                Avg Completeness
              </span>
              <span style={{ fontSize: 32, fontWeight: 800, color: completenessColor(overallCompleteness) }}>
                {overallCompleteness}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: T.dangerBg, border: `1px solid ${T.danger}30`,
          color: T.danger, fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* Products Table */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
      }}>
        {products.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
              No Product Updates Yet
            </div>
            <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
              The {agentDisplay} agent has not collected data for any products yet.
              Product updates will appear here once the case officer triggers this agent.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  {['NDC #', 'Product Name', ...stewardColumns.map(c => c.label), 'Data Completeness', 'Updated At'].map(col => (
                    <th key={col} style={{
                      padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                      color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                      whiteSpace: 'nowrap',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => {
                  const rowKey = `${product.ndc}-${product.run_id}-${idx}`;
                  const isExpanded = expandedRows.has(rowKey);
                  const completeness = computeCompleteness(product);

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => toggleRow(rowKey)}
                        style={{
                          borderBottom: `1px solid ${T.borderLight}`,
                          cursor: 'pointer',
                          background: isExpanded ? `${T.gold}08` : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = `${T.navy}04`; }}
                        onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                      >
                        {/* NDC */}
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke={T.textLight} strokeWidth="2"
                              style={{
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                flexShrink: 0,
                              }}
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                            {product.ndc}
                          </div>
                        </td>
                        {/* Product Name */}
                        <td style={{ padding: '12px 16px', color: T.textDark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {product.product_name || '--'}
                        </td>
                        {/* Steward-specific columns */}
                        {stewardColumns.map(col => (
                          <td key={col.key} style={{ padding: '12px 16px', color: T.textDark, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {(() => {
                              const val = col.render(product);
                              if (val === 'Yes') return (
                                <span style={{
                                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                                  background: '#22C55E18', color: '#22C55E',
                                }}>Yes</span>
                              );
                              if (val === 'No') return (
                                <span style={{
                                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                                  background: '#EF444418', color: '#EF4444',
                                }}>No</span>
                              );
                              return <span style={{ color: val === '--' ? T.textLight : T.textDark }}>{val}</span>;
                            })()}
                          </td>
                        ))}
                        {/* Data Completeness */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                              width: 48, height: 6, borderRadius: 3, background: `${T.border}`,
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${completeness}%`, height: '100%', borderRadius: 3,
                                background: completenessColor(completeness),
                                transition: 'width 0.3s',
                              }} />
                            </div>
                            <span style={{
                              fontSize: 12, fontWeight: 700,
                              color: completenessColor(completeness),
                            }}>
                              {completeness}%
                            </span>
                          </div>
                        </td>
                        {/* Updated At */}
                        <td style={{ padding: '12px 16px', color: T.textLight, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(product.scored_at)}
                        </td>
                      </tr>

                      {/* Expanded evidence details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={stewardColumns.length + 4} style={{ padding: 0 }}>
                            <div style={{
                              padding: '16px 24px 20px',
                              background: `linear-gradient(180deg, ${T.offWhite} 0%, ${T.white} 100%)`,
                              borderBottom: `2px solid ${T.gold}30`,
                            }}>
                              <div style={{
                                fontSize: 11, fontWeight: 700, color: T.textLight,
                                textTransform: 'uppercase', letterSpacing: '0.1em',
                                marginBottom: 12,
                              }}>
                                Collected Evidence Details
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {(product.factor_details || []).map((detail, dIdx) => (
                                  <div key={`${detail.factor}-${dIdx}`} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                    padding: '10px 14px', borderRadius: 8,
                                    border: `1px solid ${T.borderLight}`,
                                    background: detail.hit ? `${T.success}04` : T.white,
                                  }}>
                                    <div style={{
                                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                                      background: detail.disabled ? T.textLight : detail.hit ? '#22C55E' : '#EF4444',
                                      opacity: detail.disabled ? 0.3 : 1,
                                    }} />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>
                                        {detail.factor.replace(/_/g, ' ')}
                                      </div>
                                      {detail.result && (
                                        <div style={{ fontSize: 11, color: T.textLight, marginTop: 3, lineHeight: 1.5 }}>
                                          {detail.result}
                                        </div>
                                      )}
                                      {detail.raw_inputs && Object.keys(detail.raw_inputs).length > 0 && (
                                        <div style={{ marginTop: 8 }}>
                                          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', marginBottom: 4 }}>
                                            Raw Data
                                          </div>
                                          <div style={{
                                            padding: '8px 12px', borderRadius: 6,
                                            background: T.offWhite, border: `1px solid ${T.borderLight}`,
                                            fontSize: 11,
                                          }}>
                                            {Object.entries(detail.raw_inputs).map(([k, v]) => (
                                              <div key={k} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
                                                <span style={{ fontWeight: 600, color: T.textDark, minWidth: 120 }}>{k.replace(/_/g, ' ')}:</span>
                                                <span style={{ color: T.textLight, wordBreak: 'break-word' }}>
                                                  {typeof v === 'object' ? JSON.stringify(v) : String(v || '(empty)')}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <span style={{
                                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, flexShrink: 0,
                                      background: detail.disabled ? `${T.textLight}15` : detail.hit ? '#22C55E15' : '#EF444415',
                                      color: detail.disabled ? T.textLight : detail.hit ? '#22C55E' : '#EF4444',
                                    }}>
                                      {detail.disabled ? 'DISABLED' : detail.hit ? 'COLLECTED' : 'MISSING'}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* Completeness summary bar */}
                              <div style={{
                                marginTop: 12, padding: '10px 16px', borderRadius: 8,
                                background: T.navy, display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between',
                              }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
                                  Data Completeness
                                </span>
                                <span style={{
                                  fontSize: 18, fontWeight: 800,
                                  color: completenessColor(completeness),
                                }}>
                                  {completeness}%
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
